/**
 * Stable fingerprints for “persistable workspace” state — compares what would be saved in scene JSON
 * (semantics + sidebar meta + flowGraph layout), ignoring volatile fields like flowGraph.savedAt.
 */
import type { ISceneObj } from '../../../types/SceneType';
import type { FlowEdge, FlowNode } from '../types/flow-types';
import { graphToScene, mergeIfWithIndailyintIntervals } from '../converters/FlowToScene';

export interface WorkspaceSidebarMeta {
	name: string;
	room: number | string;
	roomName: string;
	image?: string;
	backgroundColor?: string;
	activeTimeIntervals?: any[];
}

function sortKeysDeep(val: unknown): unknown {
	if (val === null || typeof val !== 'object') return val;
	if (Array.isArray(val)) return val.map(sortKeysDeep);
	const o = val as Record<string, unknown>;
	const out: Record<string, unknown> = {};
	for (const k of Object.keys(o).sort()) {
		out[k] = sortKeysDeep(o[k]);
	}
	return out;
}

export function stableStringify(value: unknown): string {
	return JSON.stringify(sortKeysDeep(value));
}

/** Strip timestamps so two drafts that differ only by `savedAt` compare equal. */
function flowGraphWithoutVolatileTimestamps(flowGraph: unknown): unknown {
	if (!flowGraph || typeof flowGraph !== 'object') return flowGraph;
	const fg = flowGraph as Record<string, unknown>;
	const { savedAt: _s, ...rest } = fg;
	return rest;
}

/**
 * Builds the same semantic + layout snapshot as save (sidebar intervals + metadata merged).
 */
export function buildPersistableSceneDraft(
	baseScene: ISceneObj,
	nodes: FlowNode[],
	edges: FlowEdge[],
	meta: WorkspaceSidebarMeta,
): ISceneObj {
	const draft = graphToScene(nodes, edges, baseScene);
	draft.if = mergeIfWithIndailyintIntervals(draft.if, meta.activeTimeIntervals ?? []);
	if (draft._meta) {
		draft._meta.activeTimeIntervals = meta.activeTimeIntervals ?? [];
		draft._meta.name = (meta.name || draft._meta.name || '').trim();
		draft._meta.room = meta.room ?? draft._meta.room;
		draft._meta.roomn = meta.roomName || draft._meta.roomn || '';
		draft._meta.image = meta.image || draft._meta.image || 'fa-diagram-project';
		draft._meta.backgroundColor =
			meta.backgroundColor || draft._meta.backgroundColor || 'rgb(102, 96, 237)';
	}
	return draft;
}

/** Subset that round-trips through backend save/load for dirty detection. */
export function fingerprintPersistableWorkspace(scene: ISceneObj): string {
	const fg = (scene as any)?._meta?.flowGraph;
	const subset = {
		if: scene.if,
		do: scene.do,
		else: scene.else,
		_meta: {
			name: scene._meta?.name,
			room: scene._meta?.room,
			roomn: scene._meta?.roomn,
			image: scene._meta?.image,
			backgroundColor: scene._meta?.backgroundColor,
			activeTimeIntervals: scene._meta?.activeTimeIntervals,
			flowGraph: flowGraphWithoutVolatileTimestamps(fg),
		},
	};
	return stableStringify(subset);
}
